import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mail, Phone, Tag, X, BadgePoundSterling, Inbox, Loader2,
  AlertCircle, CheckCircle2, ExternalLink, Copy, MessageCircle, Upload, Trash2
} from 'lucide-react';
import { customerWhatsAppLink } from '../../lib/whatsapp';
import { withAdminAuth } from '../../lib/adminAuth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_FILTERS = [
  { key: 'new', label: 'New' },
  { key: 'replied', label: 'Replied' },
  { key: 'closed', label: 'Closed' },
  { key: 'all', label: 'All' },
];

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export default function ContactsManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('new');
  const [active, setActive] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importSummary, setImportSummary] = useState(null);
  const fileInputRef = useRef(null);

  // httpOnly cookie carries auth — no localStorage token needed.
  const fetchItems = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/contacts`, withAdminAuth());
      setItems(r.data?.contacts || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const handleImportFile = async (file) => {
    if (!file) return;
    setImporting(true);
    setImportSummary(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(
        `${BACKEND_URL}/api/admin/contacts/import`,
        fd,
        withAdminAuth({ headers: { 'Content-Type': 'multipart/form-data' } })
      );
      setImportSummary(r.data);
      toast.success(`Imported ${r.data.inserted} new contact${r.data.inserted === 1 ? '' : 's'}`);
      await fetchItems();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (e.message || 'Import failed'));
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const visible = items.filter((c) =>
    statusFilter === 'all' ? true : (c.status || 'new') === statusFilter
  );

  return (
    <div className="p-6 md:p-8 max-w-7xl" data-testid="contacts-page">
      <div className="mb-8 flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-light tracking-tight">Inquiries</h1>
          <p className="text-gray-500 mt-2">
            Contact-form messages, including format-specific price enquiries.
          </p>
        </div>
        <div className="flex gap-1">
          {STATUS_FILTERS.map((f) => {
            const n = items.filter((c) =>
              f.key === 'all' ? true : (c.status || 'new') === f.key
            ).length;
            return (
              <button
                key={f.key}
                onClick={() => setStatusFilter(f.key)}
                data-testid={`contacts-filter-${f.key}`}
                className={`px-3 py-1.5 text-sm rounded border transition ${
                  statusFilter === f.key
                    ? 'bg-black text-white border-black'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-black'
                }`}
              >
                {f.label} <span className="opacity-60">({n})</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Bulk import contacts from CSV/ZIP */}
      <section className="mb-6 border border-gray-200 rounded-lg p-5 bg-white" data-testid="contacts-import-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-sm font-medium uppercase tracking-[0.18em] text-gray-700 mb-1 flex items-center gap-2">
              <Upload className="w-4 h-4" /> Import contacts
            </h2>
            <p className="text-sm text-gray-500">
              Upload a CSV or ZIP exported from Outlook, Google Contacts, Mailchimp or Artlogic.
              Duplicates (matched by email) are skipped automatically.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.zip"
              data-testid="contacts-csv-input"
              className="text-sm"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleImportFile(f);
              }}
            />
            {importing && (
              <span className="inline-flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" /> Importing…
              </span>
            )}
          </div>
        </div>
        {importSummary && (
          <div
            className="mt-3 text-xs text-gray-600 space-y-1"
            data-testid="contacts-import-summary"
          >
            <div>Inserted: <strong>{importSummary.inserted}</strong></div>
            <div>Skipped (duplicate): {importSummary.skipped_existing}</div>
            <div>Skipped (invalid): {importSummary.skipped_invalid}</div>
            {importSummary.files_processed && (
              <div>Files processed: {importSummary.files_processed}</div>
            )}
          </div>
        )}
      </section>

      {error && (
        <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 flex gap-2 text-sm text-red-700" data-testid="contacts-error">
          <AlertCircle className="w-4 h-4 mt-0.5" /> {error}
        </div>
      )}

      {loading ? (
        <div className="text-gray-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : visible.length === 0 ? (
        <div className="p-8 rounded bg-gray-50 border border-gray-200 text-center text-gray-500" data-testid="contacts-empty">
          <Inbox className="w-8 h-8 mx-auto mb-2" /> No inquiries in this view.
        </div>
      ) : (
        <div className="space-y-2">
          {visible.map((c) => (
            <ContactRow key={c.id || c._id || c.email + c.createdAt} c={c} onOpen={() => setActive(c)} />
          ))}
        </div>
      )}

      <AnimatePresence>
        {active && (
          <ContactDetailDrawer
            contact={active}
            onClose={() => setActive(null)}
            onChanged={() => { setActive(null); fetchItems(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function ContactRow({ c, onOpen }) {
  const inq = (c.inquiry_type || '').toLowerCase();
  const isPriceReq = inq === 'price_request';
  const isWallPreview = inq === 'wall_preview';
  const isVideo = inq === 'video_request';
  const isArtistPack = inq === 'artist_pack';

  // Choose the small thumbnail shown left of the row.
  // Wall-preview leads use the composed PNG (admin-only endpoint), other
  // artwork-related leads use the artwork's own image.
  const token = null; // legacy variable name — auth now via httpOnly cookie
  const thumbSrc = isWallPreview && c.preview_filename
    ? null  // load via authenticated fetch below
    : (c.artwork_image_url
        ? (c.artwork_image_url.startsWith('http')
            ? c.artwork_image_url
            : `${BACKEND_URL}${c.artwork_image_url}`)
        : null);

  const [previewBlobUrl, setPreviewBlobUrl] = React.useState(null);
  React.useEffect(() => {
    if (!isWallPreview || !c.preview_filename) return;
    let cancelled = false;
    let url;
    fetch(`${BACKEND_URL}/api/admin/wall-previews/${c.preview_filename}`, {
      credentials: 'include',
    })
      .then((r) => (r.ok ? r.blob() : null))
      .then((blob) => {
        if (cancelled || !blob) return;
        url = URL.createObjectURL(blob);
        setPreviewBlobUrl(url);
      })
      .catch(() => {});
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [isWallPreview, c.preview_filename]);

  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`contact-row-${c.id || c.email}`}
      className="w-full text-left p-4 bg-white rounded-lg border border-gray-200 hover:border-black transition flex items-start gap-4"
    >
      <div className={`w-2 h-2 rounded-full mt-2 ${
        (c.status || 'new') === 'new' ? 'bg-amber-500' :
        (c.status || 'new') === 'replied' ? 'bg-blue-500' : 'bg-gray-300'
      }`} />

      {(thumbSrc || previewBlobUrl) && (
        <img
          src={previewBlobUrl || thumbSrc}
          alt=""
          className="w-14 h-14 object-cover rounded-md border border-gray-200 flex-shrink-0"
          loading="lazy"
        />
      )}

      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-3">
          <p className="font-medium truncate">{c.name}</p>
          <p className="text-xs text-gray-400 whitespace-nowrap">{fmtDate(c.createdAt)}</p>
        </div>
        <p className="text-sm text-gray-600 truncate flex items-center gap-1.5">
          <Mail className="w-3.5 h-3.5 flex-shrink-0" /> {c.email}
        </p>
        {c.phone && (
          <p className="text-sm text-gray-600 truncate flex items-center gap-1.5 mt-0.5">
            <Phone className="w-3.5 h-3.5 flex-shrink-0" /> {c.phone}
          </p>
        )}
        <div className="flex items-center gap-2 flex-wrap mt-1.5 text-xs">
          {isPriceReq && (
            <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 flex items-center gap-1">
              <BadgePoundSterling className="w-3 h-3" /> Price request
            </span>
          )}
          {isWallPreview && (
            <span className="px-2 py-0.5 rounded-full bg-violet-100 text-violet-800">
              Wall preview
            </span>
          )}
          {isVideo && (
            <span className="px-2 py-0.5 rounded-full bg-sky-100 text-sky-800">
              Video request
            </span>
          )}
          {isArtistPack && (
            <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
              Artist pack
            </span>
          )}
          {(c.artwork_title || c.artwork_artist) && (
            <span className="text-gray-500 truncate">
              {c.artwork_title}
              {c.artwork_artist ? ` · ${c.artwork_artist}` : ''}
            </span>
          )}
          {c.format_size && (
            <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">{c.format_size}</span>
          )}
          {c.format_price && (
            <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
              {(c.format_currency === 'GBP' || !c.format_currency) ? '£' : ''}
              {Number(c.format_price).toLocaleString('en-GB')}
            </span>
          )}
          {c.priority && c.priority !== 'normal' && (
            <span className={`px-2 py-0.5 rounded-full ${
              c.priority === 'high' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
            }`}>
              <Tag className="w-3 h-3 inline mr-1" />{c.priority}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

function ContactDetailDrawer({ contact, onClose, onChanged }) {
  const inq = (contact.inquiry_type || '').toLowerCase();
  const isPriceReq = inq === 'price_request';
  const isWallPreview = inq === 'wall_preview';
  const canIssueLink = isPriceReq && contact.artwork_id && contact.format_price && contact.email;

  const token = null; // legacy — cookie auth now
  const [previewBlobUrl, setPreviewBlobUrl] = React.useState(null);
  React.useEffect(() => {
    if (!isWallPreview || !contact.preview_filename) return;
    let cancelled = false;
    let url;
    fetch(`${BACKEND_URL}/api/admin/wall-previews/${contact.preview_filename}`, {
      credentials: 'include',
    })
      .then((r) => (r.ok ? r.blob() : null))
      .then((blob) => {
        if (cancelled || !blob) return;
        url = URL.createObjectURL(blob);
        setPreviewBlobUrl(url);
      })
      .catch(() => {});
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [isWallPreview, contact.preview_filename]);

  const artworkThumb = contact.artwork_image_url
    ? (contact.artwork_image_url.startsWith('http')
        ? contact.artwork_image_url
        : `${BACKEND_URL}${contact.artwork_image_url}`)
    : null;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/40 flex justify-end"
      onClick={onClose}
      data-testid="contact-detail-drawer"
    >
      <motion.div
        initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
        transition={{ type: 'tween', duration: 0.25 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white h-full overflow-y-auto p-6"
      >
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="text-xl font-medium">Inquiry</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-black"><X className="w-5 h-5" /></button>
        </div>

        <div className="bg-gray-50 rounded-lg p-4 text-sm space-y-2 mb-6">
          <p className="font-medium">{contact.name}</p>
          <p className="flex items-center gap-2 text-gray-600"><Mail className="w-3.5 h-3.5" /> <a href={`mailto:${contact.email}`} className="underline">{contact.email}</a></p>
          {contact.phone && (() => {
            const waSubject = contact.artwork_title
              ? `Hi ${contact.name?.split(' ')[0] || ''}, this is Gabrielle at Zebra One Gallery — re: your enquiry about ${contact.artwork_title}.`
              : `Hi ${contact.name?.split(' ')[0] || ''}, this is Gabrielle at Zebra One Gallery — re: your enquiry.`;
            const waHref = customerWhatsAppLink(contact.phone, waSubject);
            return (
              <p className="flex items-center gap-2 text-gray-600 flex-wrap">
                <Phone className="w-3.5 h-3.5" />
                <a href={`tel:${contact.phone}`} className="underline">{contact.phone}</a>
                {waHref && (
                  <a
                    href={waHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-whatsapp-customer-btn"
                    className="ml-2 inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full bg-[#25D366] text-white hover:opacity-90 transition-opacity"
                  >
                    <MessageCircle className="w-3 h-3" /> WhatsApp
                  </a>
                )}
              </p>
            );
          })()}
          <p className="text-xs text-gray-400">{fmtDate(contact.createdAt)}</p>
        </div>

        {(contact.artwork_title || contact.format_size) && (
          <div className="mb-6">
            <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">
              {isWallPreview ? 'Wall preview' : 'Subject'}
            </p>

            {/* Composed wall-preview image (priority) or artwork thumbnail */}
            {previewBlobUrl ? (
              <a
                href={previewBlobUrl}
                target="_blank"
                rel="noreferrer"
                className="block mb-3 border border-gray-200 rounded-md overflow-hidden hover:border-black transition-colors"
                data-testid="contact-wall-preview-thumb"
              >
                <img src={previewBlobUrl} alt="Composed wall preview" className="w-full h-auto" />
              </a>
            ) : artworkThumb ? (
              <a
                href={artworkThumb}
                target="_blank"
                rel="noreferrer"
                className="block mb-3 border border-gray-200 rounded-md overflow-hidden hover:border-black transition-colors max-w-[200px]"
                data-testid="contact-artwork-thumb"
              >
                <img src={artworkThumb} alt={contact.artwork_title || 'Artwork'} className="w-full h-auto" />
              </a>
            ) : null}

            <p className="text-sm">
              {contact.artwork_title}
              {contact.artwork_artist ? ` · ${contact.artwork_artist}` : ''}
            </p>
            {contact.format_size && (
              <p className="text-sm text-gray-600 mt-1">
                {contact.format_size}
                {contact.format_price && (
                  <> · <strong>£{Number(contact.format_price).toLocaleString('en-GB')}</strong></>
                )}
                {contact.format_sku && <> · <span className="text-gray-400">SKU {contact.format_sku}</span></>}
              </p>
            )}
            {contact.artwork_id && (
              <a
                href={`/artwork/${contact.artwork_id}`}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-gray-500 underline inline-flex items-center gap-1 mt-2 hover:text-black"
              >
                Open artwork page <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        )}

        <div className="mb-6">
          <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">Message</p>
          <p className="text-sm whitespace-pre-wrap text-gray-700">{contact.message}</p>
        </div>

        {canIssueLink && (
          <IssueStripeLink contact={contact} onIssued={onChanged} />
        )}

        <ContactActions contact={contact} onChanged={onChanged} />
      </motion.div>
    </motion.div>
  );
}

function ContactActions({ contact, onChanged }) {
  const [busy, setBusy] = useState(false);
  const setStatus = async (status) => {
    setBusy(true);
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/contacts/${contact.id}/status`,
        { status },
        withAdminAuth()
      );
      onChanged();
    } finally { setBusy(false); }
  };
  const cur = contact.status || 'new';
  return (
    <div className="border-t pt-4 mt-4">
      <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">Update status</p>
      <div className="grid grid-cols-3 gap-1.5">
        {['new', 'replied', 'closed'].map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            disabled={busy || cur === s}
            data-testid={`contacts-status-${s}`}
            className={`text-xs py-2 rounded border ${
              cur === s
                ? 'bg-black text-white border-black cursor-default'
                : 'bg-white text-gray-700 border-gray-300 hover:border-black disabled:opacity-50'
            }`}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>
    </div>
  );
}

function IssueStripeLink({ contact, onIssued }) {
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState(null);
  const [error, setError] = useState(null);
  const [agreedPrice, setAgreedPrice] = useState(String(contact.format_price || ''));

  const issue = async () => {
    setBusy(true); setError(null);
    try {
      const price = parseFloat(agreedPrice);
      if (!price || price <= 0) throw new Error('Enter the agreed price.');
      const r = await axios.post(
        `${BACKEND_URL}/api/admin/payments/checkout/create`,
        {
          artwork_id: contact.artwork_id,
          agreed_price: price,
          currency: (contact.format_currency || 'gbp').toLowerCase(),
          buyer_email: contact.email,
          buyer_name: contact.name || undefined,
          origin_url: window.location.origin,
          notes: `Inquiry ${contact.id}${contact.format_size ? ` · ${contact.format_size}` : ''}${contact.format_sku ? ` · SKU ${contact.format_sku}` : ''}`,
        },
        withAdminAuth()
      );
      setLink(r.data.checkout_url);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(link); } catch (err) { if (process.env.NODE_ENV !== 'production') console.warn('[contacts] clipboard write failed:', err); }
  };

  return (
    <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-4 mb-6" data-testid="contact-issue-stripe-block">
      <p className="text-sm font-medium mb-1 flex items-center gap-2">
        <BadgePoundSterling className="w-4 h-4" /> Issue payment link
      </p>
      <p className="text-xs text-gray-600 mb-3">
        Pre-filled from this inquiry. Adjust the price if you've negotiated.
      </p>
      <div className="flex gap-2 mb-2">
        <span className="px-3 py-2 bg-white border border-gray-200 rounded text-sm text-gray-500">£</span>
        <input
          type="number" step="0.01" min="0"
          value={agreedPrice}
          onChange={(e) => setAgreedPrice(e.target.value)}
          data-testid="contact-issue-price-input"
          className="flex-1 text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:border-black"
        />
      </div>
      <button
        onClick={issue}
        disabled={busy || !agreedPrice}
        data-testid="contact-issue-stripe-btn"
        className="w-full py-2 bg-emerald-700 text-white text-sm rounded hover:bg-emerald-800 disabled:bg-gray-300 flex items-center justify-center gap-2"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <BadgePoundSterling className="w-4 h-4" />}
        Generate Stripe link
      </button>
      {link && (
        <div className="mt-3 bg-white border border-emerald-200 rounded p-2 text-xs break-all" data-testid="contact-issue-stripe-link">
          <div className="flex items-center gap-2 mb-2">
            <a href={link} target="_blank" rel="noreferrer" className="text-emerald-700 underline flex items-center gap-1">
              <ExternalLink className="w-3 h-3" /> Open
            </a>
            <button onClick={copy} className="text-gray-600 underline flex items-center gap-1">
              <Copy className="w-3 h-3" /> Copy
            </button>
          </div>
          <code className="text-gray-700">{link}</code>
        </div>
      )}
      {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
    </div>
  );
}
